import type { Dependency, DisplayRow, GanttMilestone, GanttPeriod, GanttTask } from '../types/gantt.types'
import { addDays, daysBetween, formatISO, groupTasksIntoRows, type GanttRow } from '../views/scheduleUtils'
import { BUFFER_DAYS, DN, EXTRA_PAD_DAYS, LEAD_PAD_DAYS, PDF_DW } from './gantt.const'

// 간트 화면 순수 계산 모음(React 의존 없음). 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.
// daysBetween·addDays·formatISO 는 scheduleUtils.ts 의 같은 본문을 쓴다(H-17 — 로컬 중복 제거, 다른 파일의 import 경로 유지를 위해 다시 내보낸다).
export { addDays, daysBetween, formatISO }

// bounds 확장 후 바뀔 DW를 미리 계산 (drag.startX 보정용)
export function computeDW(cw: number, period: string, totalDays: number): number {
  if (cw <= 200) return PDF_DW
  // 전체 보기 실제 칸 수 = 앞 2주(LEAD_PAD_DAYS) + 표시 기간 + 뒤 여유(EXTRA_PAD_DAYS) — computeVisibleRange 와 같은 기준
  const padded = totalDays + LEAD_PAD_DAYS + EXTRA_PAD_DAYS
  const divisor = period === '전체'
    ? Math.max(padded, 1)
    : Math.min((period === '1주' ? 7 : period === '2주' ? 14 : 30) + 2 * EXTRA_PAD_DAYS, Math.max(padded, 1))
  const raw = (cw - 212) / divisor
  return period === '전체' ? Math.max(1, raw) : Math.max(1, Math.floor(raw))
}
export function fmtDate(d: Date) {
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`
}
// BFS 연쇄 조회·재배치 계산·검증은 scheduleUtils.ts의 공용 로직을 쓴다(getCascadeIds,
// computeCascadeReschedule, validateScheduleEndWarn, validatePredecessorBlock — 위에서 import).

/* ── Dependency arrow path ───────────────────────────────────── */
// 선행 공종 막대의 "아래쪽 가운데"에서 출발해 아래로 내려간 뒤, 후행 공종 쪽(왼쪽 가장자리)으로
// 꺾어 들어간다(막대 오른쪽에서 출발하던 방식 대신).
export function arrowPath(x0: number, y0: number, x3: number, y3: number): string {
  const r = 5
  const dy = y3 - y0, dx = x3 - x0
  if (Math.abs(dy) < r * 2 || Math.abs(dx) < 1) return `M ${x0},${y0} L ${x3},${y3}`
  const vSign = dy >= 0 ? 1 : -1, hSign = dx >= 0 ? 1 : -1
  return [`M ${x0},${y0}`, `L ${x0},${y3 - r*vSign}`,
          `Q ${x0},${y3} ${x0 + r*hSign},${y3}`, `L ${x3},${y3}`].join(' ')
}

/** 새 창 HTML 문자열에 삽입되는 사용자 유래 값 이스케이프 (인쇄·PDF) */
export function escapeHtml(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string))
}

// 표시 구간(시작·끝·총일수) 계산 — 순수 함수. ref 대입(clampedGanttOffsetRef·visibleDayRangeRef)은 호출 쪽에서 한다.
export function computeVisibleRange(p: { ganttBounds: { start: Date; end: Date }; ganttPeriod: GanttPeriod; ganttOffset: number; today: Date }) {
  const { ganttBounds, ganttPeriod, ganttOffset, today } = p
  const _fullStart = ganttBounds.start
  const _fullEnd   = ganttBounds.end
  let ganttStart: Date, ganttEnd: Date
  let clampedOffset: number | null = null
  if (ganttPeriod === '전체') {
    // 전체 범위 앞은 2주(LEAD_PAD_DAYS), 뒤는 EXTRA_PAD_DAYS만큼 여유를 더 보여준다
    ganttStart = addDays(_fullStart, -LEAD_PAD_DAYS)
    ganttEnd   = addDays(_fullEnd,   EXTRA_PAD_DAYS)
  } else {
    const days      = ganttPeriod === '1주' ? 7 : ganttPeriod === '2주' ? 14 : 30
    const baseStart = today < _fullStart ? new Date(_fullStart) : new Date(today)
    const panned    = addDays(baseStart, ganttOffset)
    // 팬은 표시 기간 2주 앞까지 갈 수 있게 — 가시 시작(clampedStart − EXTRA_PAD_DAYS)이 _fullStart − LEAD_PAD_DAYS 가 되는 지점
    const minStart  = addDays(_fullStart, EXTRA_PAD_DAYS - LEAD_PAD_DAYS)
    const maxStart  = new Date(Math.max(_fullStart.getTime(), addDays(_fullEnd, -(days - 1)).getTime()))
    let clampedStart: Date
    if (panned < minStart)      clampedStart = new Date(minStart)
    else if (panned > maxStart) clampedStart = new Date(maxStart)
    else                        clampedStart = new Date(panned)
    // 좌우 BUFFER_DAYS 여분 컬럼 렌더링 → 부드러운 scrollLeft 팬 지원.
    // 그 안쪽(실제 가시 구간)의 시작·끝 날짜 앞뒤로도 EXTRA_PAD_DAYS만큼 더 보여준다.
    clampedOffset = Math.round((clampedStart.getTime() - baseStart.getTime()) / 86400000)
    ganttStart = addDays(clampedStart, -EXTRA_PAD_DAYS - BUFFER_DAYS)
    ganttEnd   = addDays(clampedStart, days - 1 + EXTRA_PAD_DAYS + BUFFER_DAYS)
  }
  const totalDays  = Math.max(daysBetween(ganttStart, ganttEnd) + 1, 1)
  // 실제 화면에 보이는 날짜 범위(day index 기준) — 전체 뷰는 전체 범위, 1주/2주/월(팬 모드)은
  // 좌우 BUFFER_DAYS를 뺀 진짜 가시 구간만 해당. 마일스톤 드래그 시 이 범위로 clamp한다.
  const visibleDayRange: [number, number] = ganttPeriod === '전체'
    ? [0, totalDays - 1]
    : [BUFFER_DAYS, totalDays - 1 - BUFFER_DAYS]
  return { ganttStart, ganttEnd, totalDays, clampedOffset, visibleDayRange }
}

// 일자 배열(날짜 헤더·격자) — 순수 함수
export function buildDays(p: { ganttStart: Date; totalDays: number; today: Date; holidays: Set<string> }) {
  const { ganttStart, totalDays, today, holidays } = p
  const DAYS = Array.from({ length: totalDays }, (_, i) => {
    const d = new Date(ganttStart)
    d.setDate(ganttStart.getDate() + i); d.setHours(0,0,0,0)
    const iso = formatISO(d)
    return { lbl: `${d.getMonth()+1}/${d.getDate()}`, dow: DN[d.getDay()], y: d.getFullYear(), m: d.getMonth() + 1,
             su: d.getDay()===0, sa: d.getDay()===6, td: d.getTime()===today.getTime(),
             holiday: holidays.has(iso) }
  })
  return DAYS
}

// 마일스톤 날짜 → 목록 매핑과 마일스톤 행 높이 — 순수 함수
export function buildMilestoneDayMap(p: { milestones: GanttMilestone[]; ganttStart: Date; totalDays: number }) {
  const { milestones, ganttStart, totalDays } = p
  const milestoneDayMap = new Map<number, GanttMilestone[]>()
  milestones.forEach(m => {
    const d = new Date(m.milestoneDate); d.setHours(0,0,0,0)
    const day = Math.round((d.getTime() - ganttStart.getTime()) / 86400000)
    if (day >= 0 && day < totalDays) {
      const arr = milestoneDayMap.get(day) ?? []
      arr.push(m)
      milestoneDayMap.set(day, arr)
    }
  })
  // 같은 날짜에 마일스톤이 여러 개면 한 줄에 겹쳐 보이므로, 세로로 쌓아 보여줄 수 있게
  // 행 높이를 그 중 가장 많은 개수에 맞춰 늘린다.
  const MILESTONE_ROW_UNIT_H = 26
  const milestoneRowMaxCount = Math.max(1, ...Array.from(milestoneDayMap.values()).map(arr => arr.length))
  const milestoneRowH = MILESTONE_ROW_UNIT_H * milestoneRowMaxCount
  return { milestoneDayMap, milestoneRowH }
}

// 화면에 그릴 행 목록(펼침 반영)과 투어 대상 첫 바 — 순수 함수
export function buildDisplayRows(p: { tasks: GanttTask[]; sigOn: boolean; signal: { onlyRY: boolean; ryTaskIds: Set<number> }; expandedRows: Set<string> }) {
  const { tasks, sigOn, signal, expandedRows } = p
  const rows: GanttRow<GanttTask>[] = groupTasksIntoRows(sigOn && signal.onlyRY ? tasks.filter(t => signal.ryTaskIds.has(t.taskId)) : tasks)

  // 실제로 화면에 그릴 "행" 목록 — 바가 2개 이상인 공종이 펼쳐진 상태면, 그 공종의 대표(요약)
  // 행 하나에 이어서 바마다 완전히 독립된 행을 하나씩 추가한다(펼쳐진 개별 행은 bars가 정확히
  // 1개뿐이라 단일 바 행과 렌더 코드를 100% 그대로 공유한다). 접혀 있거나 바가 1개뿐이면 원래
  // 그룹 그대로 한 행이다.
  const displayRows: DisplayRow[] = []
  for (const row of rows) {
    if (row.bars.length > 1 && expandedRows.has(row.workTypeCode)) {
      displayRows.push({ ...row, isGroupHeader: true })
      for (const bar of row.bars) {
        displayRows.push({ workTypeCode: row.workTypeCode, workTypeName: bar.workTypeName, bars: [bar], isGroupHeader: false, isExpandedChild: true })
      }
    } else {
      displayRows.push({ ...row, isGroupHeader: false })
    }
  }
  // 화면 가이드(온보딩 투어)가 "일정 막대"를 가리킬 때 붙잡을 대상 — 실제로 바가 그려지는
  // 첫 번째 행(대표/요약 행은 바를 그리지 않으므로 제외)의 첫 바.
  const firstBarTaskId = displayRows.find(r => !r.isGroupHeader && r.bars.length > 0)?.bars[0]?.taskId ?? null
  return { displayRows, firstBarTaskId }
}

// 선후관계 화살표(겹침 회피 레인 포함) — 순수 함수
export function buildArrows(p: { deps: Dependency[]; tasks: GanttTask[]; taskIdxMap: Map<number, number>; getTaskSL: (task: GanttTask) => { s: number; l: number; rawL: number }; draggingTaskId: number | null; draggingOffset: number; resizingTaskId: number | null; resizingOffset: number; DW: number; rowH: number; barTop: number }) {
  const { deps, tasks, taskIdxMap, getTaskSL, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset, DW, rowH, barTop } = p
  // 같은 후행 공종으로 여러 화살표가 도착해도 도착점(x3,y3)은 항상 하나로 모은다 — 대신
  // 화살표마다 선행 공종의 색을 그대로 써서(stroke=pred.color) 어떤 공종에서 온 선인지는
  // 색으로 구분한다(도착 지점을 벌리지 않음).
  const arrows = deps
    .filter(d => d.dependencyType === 'FS')
    .map(d => {
      const pi = taskIdxMap.get(d.predecessorTaskId), si = taskIdxMap.get(d.successorTaskId)
      if (pi===undefined || si===undefined) return null
      const pred = tasks.find(t => t.taskId === d.predecessorTaskId)
      const succ = tasks.find(t => t.taskId === d.successorTaskId)
      if (!pred || !succ) return null
      const { s: ps, l: pl } = getTaskSL(pred)
      const { s: ss, l: sl } = getTaskSL(succ)
      if (pl === 0 || sl === 0) return null
      const predS = pred.taskId===draggingTaskId ? ps + draggingOffset : ps
      const succS = succ.taskId===draggingTaskId ? ss + draggingOffset : ss
      // 선행 막대를 리사이즈하는 동안은 늘어난/줄어든 길이만큼 화살표 출발점도 같이 따라온다.
      const predL = pred.taskId===resizingTaskId ? Math.max(1, pl + resizingOffset) : pl
      return {
        // 선행 막대의 "아래쪽 가운데"에서 출발 (기존엔 오른쪽 가운데)
        x0: (predS + predL / 2) * DW, y0: pi * rowH + barTop + 20,
        x3: succS * DW,            y3: si * rowH + rowH / 2,
        color: pred.color, key: d.dependencyId, pi,
      }
    })
    .filter(Boolean) as { x0:number; y0:number; x3:number; y3:number; color:string; key:number; pi:number }[]

  // 서로 다른(선행 공종이 다른) 화살표인데 세로로 내려가는 칸이 겹치면, 위에서 이미 그 칸을
  // 지나가는 화살표가 있는 쪽을 옆으로 살짝 밀어서 둘이 나란히 곧게 지나가도록 한다
  // (같은 공종에서 나가는 화살표끼리는 원래대로 한 점에서 그대로 겹쳐 출발한다).
  const LANE_X = 12, LANE_STEP = 5
  const laneActive: { x0: number; hi: number; pi: number }[] = []
  arrows
    .slice()
    .sort((a, b) => Math.min(a.y0, a.y3) - Math.min(b.y0, b.y3))
    .forEach(a => {
      const lo = Math.min(a.y0, a.y3), hi = Math.max(a.y0, a.y3)
      for (let i = laneActive.length - 1; i >= 0; i--) if (laneActive[i].hi <= lo) laneActive.splice(i, 1)
      const conflict = laneActive.find(e => e.pi !== a.pi && Math.abs(e.x0 - a.x0) < LANE_X)
      if (conflict) a.x0 += a.x0 >= conflict.x0 ? LANE_STEP : -LANE_STEP
      laneActive.push({ x0: a.x0, hi, pi: a.pi })
    })
  return arrows
}

// 선후관계 커넥터(위/아래 원) 좌표 — 순수 함수
export function buildConnectorPoints(p: { tasks: GanttTask[]; taskIdxMap: Map<number, number>; getTaskSL: (task: GanttTask) => { s: number; l: number; rawL: number }; draggingTaskId: number | null; draggingOffset: number; resizingTaskId: number | null; resizingOffset: number; DW: number; rowH: number; barTop: number }) {
  const { tasks, taskIdxMap, getTaskSL, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset, DW, rowH, barTop } = p
  const connectorPoints: { taskId: number; edge: 'top' | 'bottom'; x: number; y: number; color: string }[] = []
  taskIdxMap.forEach((ri, taskId) => {
    const task = tasks.find(t => t.taskId === taskId)
    if (!task) return
    const { s, l } = getTaskSL(task)
    if (l === 0) return
    const effS = task.taskId === draggingTaskId ? s + draggingOffset : s
    const effL = task.taskId === resizingTaskId ? Math.max(1, l + resizingOffset) : l
    const cx = (effS + effL / 2) * DW
    connectorPoints.push({ taskId, edge: 'top',    x: cx, y: ri * rowH + barTop,      color: task.color })
    connectorPoints.push({ taskId, edge: 'bottom', x: cx, y: ri * rowH + barTop + 20, color: task.color })
  })
  return connectorPoints
}

// 대기일(leadTime) 회색 막대 — 순수 함수
export function buildFloatBars(p: { tasks: GanttTask[]; taskIdxMap: Map<number, number>; draggingTaskId: number | null; draggingOffset: number; resizingTaskId: number | null; resizingOffset: number; ganttStart: Date; totalDays: number; DW: number }) {
  const { tasks, taskIdxMap, draggingTaskId, draggingOffset, resizingTaskId, resizingOffset, ganttStart, totalDays, DW } = p
  const floatBars = tasks
    .map(t => {
      if (!t.leadTime || t.leadTime <= 0) return null
      const rowIdx = taskIdxMap.get(t.taskId)
      if (rowIdx === undefined) return null

      // 대기일 바가 붙는 공정을 드래그하거나(이동) 리사이즈하는(소요일 변경) 동안은, 실제
      // 저장 전이라도 그 오프셋만큼 같이 따라오도록 시각적 종료일에 offset을 더한다.
      const effectiveEnd = t.taskId === draggingTaskId ? addDays(t.endDate, draggingOffset)
        : t.taskId === resizingTaskId ? addDays(t.endDate, resizingOffset)
        : t.endDate
      const gapStart = addDays(effectiveEnd, 1)
      const gapEnd    = addDays(effectiveEnd, t.leadTime)

      const rawS = Math.max(0, daysBetween(ganttStart, gapStart))
      const l    = Math.max(0, Math.min(daysBetween(ganttStart, gapEnd) + 1, totalDays) - rawS)
      if (l <= 0) return null
      return { rowIdx, x: rawS * DW + 1, width: l * DW - 2, key: t.taskId }
    })
    .filter(Boolean) as { rowIdx: number; x: number; width: number; key: number }[]
  return floatBars
}
